import type { ReportEntity } from './types';
import { combineTemplateParts, splitTemplate, type TemplateParts } from './templateParts';

const ENTITY_COLORS = [
  'indigo',
  'emerald',
  'amber',
  'rose',
  'cyan',
  'violet',
  'orange',
  'teal',
];

export function getEntityColor(index: number): string {
  return ENTITY_COLORS[index % ENTITY_COLORS.length];
}

export function resolvePath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

const LEGACY_ENTITY_PLACEHOLDER_RE = /\{\{([^}]+)\}\}/g;
/** Matches `{entity.field}` without touching CSS blocks like `{ margin: 0 }`. */
const ENTITY_PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z0-9_]+)*)\}/g;

export function formatEntityPlaceholder(path: string): string {
  return `{${path}}`;
}

/** Escape interpolated values so JSON/quotes are safe inside HTML attributes and text nodes. */
export function escapeHtmlInterpolatedValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function resolveEntityPlaceholder(
  trimmed: string,
  entityMap: Map<string, Record<string, unknown>>
): string | undefined {
  const dotIndex = trimmed.indexOf('.');
  if (dotIndex === -1) {
    const data = entityMap.get(trimmed.toLowerCase());
    if (data === undefined) return undefined;
    const raw = typeof data === 'object' ? JSON.stringify(data) : String(data);
    return escapeHtmlInterpolatedValue(raw);
  }

  const entityName = trimmed.slice(0, dotIndex);
  const path = trimmed.slice(dotIndex + 1);
  const data = entityMap.get(entityName.toLowerCase());
  if (data === undefined) return undefined;

  const value = resolvePath(data, path);
  if (value === undefined || value === null) return '';
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return escapeHtmlInterpolatedValue(raw);
}

export function interpolateTemplate(
  html: string,
  entities: ReportEntity[]
): string {
  const entityMap = new Map(
    entities.map((e) => [e.name.toLowerCase(), e.data])
  );

  const withLegacy = html.replace(LEGACY_ENTITY_PLACEHOLDER_RE, (_, rawPath: string) => {
    const trimmed = rawPath.trim();
    const resolved = resolveEntityPlaceholder(trimmed, entityMap);
    if (resolved === undefined) return `{{${trimmed}}}`;
    return resolved;
  });

  return withLegacy.replace(ENTITY_PLACEHOLDER_RE, (match, rawPath: string) => {
    const trimmed = rawPath.trim();
    const resolved = resolveEntityPlaceholder(trimmed, entityMap);
    if (resolved === undefined) return match;
    return resolved;
  });
}

/** Convert JSX `className` to HTML `class` so CSS selectors apply in iframe preview. */
export function normalizeReportMarkup(html: string): string {
  let result = html.replace(/\sclassName(\s*=\s*)(["'])/gi, ' class$1$2');

  result = result.replace(
    /<([a-zA-Z][\w:-]*)([^>]*?)>/g,
    (full, tagName: string, attrs: string) => {
      const classMatch = attrs.match(/\sclass=(["'])([\s\S]*?)\1/i);
      const classNameMatch = attrs.match(/\sclassName=(["'])([\s\S]*?)\1/i);
      if (!classMatch || !classNameMatch) return full;

      const merged = `${classMatch[2]} ${classNameMatch[2]}`.trim();
      const quote = classMatch[1];
      let nextAttrs = attrs
        .replace(/\sclassName=(["'])([\s\S]*?)\1/i, '')
        .replace(/\sclass=(["'])([\s\S]*?)\1/i, '');
      nextAttrs = ` class=${quote}${merged}${quote}${nextAttrs}`;
      return `<${tagName}${nextAttrs}>`;
    }
  );

  return result;
}

/** CSS class on elements whose `data-*` attrs carry `{entity.path}` bindings for preview JS. */
export const REPORT_MODAL_DATA_CLASSES = ['modal-data', 'model-data'] as const;

/** iframe sandbox tokens — modals required for alert(); same-origin bridges console to parent. */
export const REPORT_PREVIEW_IFRAME_SANDBOX = 'allow-same-origin allow-scripts allow-modals';

function buildEntityMapForPreview(entities: ReportEntity[]): Record<string, Record<string, unknown>> {
  const map: Record<string, Record<string, unknown>> = {};
  for (const entity of entities) {
    map[entity.name] = entity.data;
  }
  return map;
}

function buildPreviewEntityScript(entities: ReportEntity[]): string {
  return `window.__REPORT_ENTITIES__ = ${JSON.stringify(buildEntityMapForPreview(entities))};`;
}

function buildPreviewConsoleBridgeScript(): string {
  return `(function () {
  var prefix = '[Report preview]';
  ['log', 'info', 'warn', 'error', 'debug'].forEach(function (method) {
    var original = console[method] && console[method].bind(console);
    console[method] = function () {
      var args = Array.prototype.slice.call(arguments);
      if (original) original.apply(console, args);
      try {
        if (window.parent && window.parent !== window && window.parent.console) {
          var parentMethod = window.parent.console[method];
          if (parentMethod) parentMethod.apply(window.parent.console, [prefix].concat(args));
        }
      } catch (e) {
        /* ignore parent access errors */
      }
    };
  });
})();`;
}

function buildPreviewModalDataHydrationScript(): string {
  return `(function () {
  function resolvePath(obj, path) {
    if (!path) return obj;
    return path.split('.').reduce(function (cur, key) {
      return cur != null && typeof cur === 'object' ? cur[key] : undefined;
    }, obj);
  }
  function resolveBinding(raw, entities) {
    var trimmed = String(raw).trim();
    var dot = trimmed.indexOf('.');
    var entityName = dot === -1 ? trimmed : trimmed.slice(0, dot);
    var path = dot === -1 ? '' : trimmed.slice(dot + 1);
    var data = entities[entityName];
    if (data === undefined) {
      var target = entityName.toLowerCase();
      for (var key in entities) {
        if (Object.prototype.hasOwnProperty.call(entities, key) && key.toLowerCase() === target) {
          data = entities[key];
          break;
        }
      }
    }
    if (data === undefined) return undefined;
    if (!path) {
      return typeof data === 'object' ? JSON.stringify(data) : String(data);
    }
    var value = resolvePath(data, path);
    if (value === undefined || value === null) return '';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  function interpolateValue(value, entities) {
    return String(value).replace(/\\{([a-zA-Z_][a-zA-Z0-9_]*(?:\\.[a-zA-Z0-9_]+)*)\\}/g, function (match, rawPath) {
      var resolved = resolveBinding(rawPath.trim(), entities);
      return resolved === undefined ? match : resolved;
    });
  }
  function hydrateModalData() {
    var entities = window.__REPORT_ENTITIES__ || {};
    var selector = ${JSON.stringify(REPORT_MODAL_DATA_CLASSES.map((c) => `.${c}`).join(', '))};
    document.querySelectorAll(selector).forEach(function (el) {
      Array.from(el.attributes).forEach(function (attr) {
        if (!attr.name.startsWith('data-')) return;
        var next = interpolateValue(attr.value, entities);
        if (next !== attr.value) el.setAttribute(attr.name, next);
      });
    });
  }
  window.__hydrateReportModalData = hydrateModalData;
})();`;
}

function buildPreviewUserScriptRunner(userJs: string): string {
  const trimmed = userJs.trim();
  if (!trimmed) {
    return `(function () {
  function boot() {
    if (typeof window.__hydrateReportModalData === 'function') window.__hydrateReportModalData();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();`;
  }

  return `(function () {
  function runUserReportScript() {
    try {
${trimmed}
    } catch (err) {
      console.error('[Report preview]', err);
    }
  }
  function boot() {
    if (typeof window.__hydrateReportModalData === 'function') window.__hydrateReportModalData();
    runUserReportScript();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();`;
}

function buildPreviewRuntimeScript(entities: ReportEntity[], userJs: string): string {
  return [
    buildPreviewEntityScript(entities),
    buildPreviewConsoleBridgeScript(),
    buildPreviewModalDataHydrationScript(),
    buildPreviewUserScriptRunner(userJs),
  ].join('\n\n');
}

export function buildReportPreviewHtml(
  parts: TemplateParts,
  entities: ReportEntity[]
): string {
  const interpolatedHtml = interpolateTemplate(
    normalizeReportMarkup(parts.html),
    entities
  );

  return combineTemplateParts({
    html: interpolatedHtml,
    css: parts.css,
    js: buildPreviewRuntimeScript(entities, parts.js),
  });
}

export function buildReportPreviewHtmlFromDocument(
  htmlTemplate: string,
  entities: ReportEntity[]
): string {
  const parts = splitTemplate(normalizeReportMarkup(htmlTemplate));
  return buildReportPreviewHtml(parts, entities);
}

export function flattenEntityPaths(
  obj: unknown,
  prefix = ''
): string[] {
  if (obj == null || typeof obj !== 'object') {
    return prefix ? [prefix] : [];
  }

  if (Array.isArray(obj)) {
    return prefix ? [`${prefix}[*]`] : [];
  }

  const paths: string[] = [];
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const next = prefix ? `${prefix}.${key}` : key;
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(...flattenEntityPaths(value, next));
    } else {
      paths.push(next);
    }
  }
  return paths;
}

export type EntityJsonData = Record<string, unknown> | unknown[];

export function validateEntityJson(json: string): { valid: boolean; data?: EntityJsonData; error?: string } {
  try {
    const parsed = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object') {
      return { valid: false, error: 'JSON must be an object or array' };
    }
    return { valid: true, data: parsed as EntityJsonData };
  } catch {
    return { valid: false, error: 'Invalid JSON syntax' };
  }
}

export function formatRelativeDate(iso?: string): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

export const UNIVERSAL_REPORT_ID = '00000000-0000-0000-0000-000000000000';

export function generateReportId(): string {
  return crypto.randomUUID();
}

const ANALYTICS_HUB_VIEW_BASE =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:5174'
    : 'https://AnalyticsHub.selficai.com';

export function getAnalyticsHubLoadUrl(reportId: string): string {
  return `${ANALYTICS_HUB_VIEW_BASE}/analyticshub/load/${encodeURIComponent(reportId.trim())}`;
}

export function getAnalyticsHubEmbedUrl(reportId: string, token?: string | null): string {
  const url = getAnalyticsHubLoadUrl(reportId);
  if (!token?.trim()) return url;
  return `${url}?token=${encodeURIComponent(token.trim())}`;
}

export function getSessionAuthToken(): string {
  return localStorage.getItem('token') ?? '';
}

export function getReportAnalyticsHubLoadUrl(report: { id: string }): string {
  return getAnalyticsHubLoadUrl(report.id);
}

export function getReportAnalyticsHubViewUrl(
  report: { id: string },
  token?: string | null
): string {
  return getAnalyticsHubEmbedUrl(report.id, token ?? getSessionAuthToken());
}

export function getReportAnalyticsHubEmbedUrl(
  report: { id: string },
  token?: string | null
): string {
  return getAnalyticsHubEmbedUrl(report.id, token);
}
